<template>
  <section class="payment-split">
    <div class="left">
      <div class="title">{{ t('title.payment_mixed') }}</div>
      <ul class="button-list">
        <li>
          <BasePaymentButton :disabled="pointButtonDisabled" @button-click="handlePointClick">
            <template #icon><img src="@/assets/images/icon-payment-point.svg" alt="포인트 결제 이미지" /></template>
            {{ t('button.pay_point') }}
          </BasePaymentButton>
        </li>
        <li v-for="n in 3">
          <BasePaymentButton />
        </li>
        <li>
          <BasePaymentButton @button-click="handleCardClick">
            <template #icon><img src="@/assets/images/icon-payment-card.svg" alt="카드 이미지" /></template>
            {{ t('button.pay_card') }}
          </BasePaymentButton>
        </li>
        <li v-if="configStore.getIsCashPaymentUsed()">
          <BasePaymentButton @button-click="handleCashClick">
            <template #icon><img src="@/assets/images/icon-payment-cash.svg" alt="현금 결제 이미지" /></template>
            {{ t('button.pay_cash') }}
            <template v-if="configStore.config.CashPaymentDescription" #desc>
              {{ configStore.config.CashPaymentDescription }}
            </template>
          </BasePaymentButton>
        </li>
        <li v-if="configStore.config.IsEasyPaymentUsed">
          <BasePaymentButton @button-click="handleEasyClick">
            <template #icon><img src="@/assets/images/icon-payment-easy.png" alt="간편 결제 이미지" /></template>
            {{ t('button.pay_easy') }}
          </BasePaymentButton>
        </li>
        <li v-for="n in remainingButtonCount">
          <BasePaymentButton />
        </li>
      </ul>
    </div>
    <div class="right">
      <div class="header">
        <label>{{ t('label.payment_amount') }}</label>
        <span class="amount">{{ formatCurrency(paidAmount) }}</span>
      </div>
      <ul class="main">
        <li v-for="(split, index) in paymentStore.summary.splits" :key="index">
          <div class="payment-text">
            <div>{{ PaymentMethodTexts[split.method] }}</div>
            <div class="amount">{{ formatCurrency(split.amount) }}</div>
          </div>
          <button class="cancel-button" @click="handleCancelPaymentClick(index)">
            <img src="@/assets/images/icon-remove.svg" alt="결제 취소 버튼" />
          </button>
        </li>
      </ul>
      <div class="footer">
        <div class="rest-amount">
          <label>{{ t('label.remaining_amount') }}</label>
          <span class="amount">{{ formatCurrency(paymentStore.remainingAmount) }}</span>
        </div>
        <BaseButton variant="primary" :disabled="backButtonDisabled" class="outline" @button-click="handlePrevClick">
          <template #icon>
            <svg xmlns="http://www.w3.org/2000/svg" width="29.442" height="25.247" viewBox="0 0 29.442 25.247">
              <path
                id="arrow-left-solid"
                d="M.641,75.063a2.106,2.106,0,0,0,0,2.977L11.157,88.556a2.105,2.105,0,0,0,2.977-2.977L7.2,78.652H27.364a2.1,2.1,0,1,0,0-4.206H7.207l6.921-6.927a2.105,2.105,0,1,0-2.977-2.977L.635,75.057Z"
                transform="translate(-0.025 -63.925)"
              />
            </svg>
          </template>
          {{ t('button.prev') }}
        </BaseButton>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
  import { getCustomer, useCancelPoints } from '@/api/server';
  import BaseButton from '@/components/base/BaseButton.vue';
  import BasePaymentButton from '@/components/base/BasePaymentButton.vue';
  import CustomerSearch from '@/components/CustomerSearch.vue';
  import PaymentEasyModal from '@/components/PaymentEasyModal.vue';
  import PaymentPointModal from '@/components/PaymentPointModal.vue';
  import { useOrder } from '@/composable/useOrder';
  import { usePayment } from '@/composable/usePayment';
  import { PaymentMethod, PaymentMethodTexts } from '@/enums/PaymentMethod';
  import OrderCloudCustomer from '@/interfaces/OrderCloudCustomer';
  import RequestPoint from '@/interfaces/RequestPoint';
  import { useCartStore } from '@/stores/cart';
  import { useCommonStore } from '@/stores/common';
  import { useConfigStore } from '@/stores/config';
  import { useModalStore } from '@/stores/modal';
  import { useOrderStore } from '@/stores/order';
  import { usePaymentStore } from '@/stores/payment';
  import { formatCurrency } from '@/utils';
  import { computed, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import OrderComplete from './OrderComplete.vue';
  import PaymentCardModal from './PaymentCardModal.vue';

  const { t } = useI18n();
  const commonStore = useCommonStore();
  const configStore = useConfigStore();
  const modalStore = useModalStore();
  const cartStore = useCartStore();
  const orderStore = useOrderStore();
  const paymentStore = usePaymentStore();

  const { openReceiptOrComplete } = useOrder();
  const { createPaymentSplit } = usePayment();

  // 결제 버튼 중 나머지 버튼 개수 (최대 8개)
  const remainingButtonCount = computed(
    () => 3 - (configStore.getIsCashPaymentUsed() ? 1 : 0) - (configStore.config.IsEasyPaymentUsed ? 1 : 0),
  );

  // 결제 완료한 금액
  const paidAmount = computed(() => paymentStore.summary.amount - paymentStore.remainingAmount);
  // 이전 버튼 비활성화 여부
  const backButtonDisabled = computed(() => paymentStore.summary.splits.length > 0);
  // 포인트 결제 버튼 비활성화 여부
  const pointButtonDisabled = computed(
    () =>
      orderStore.order?.Payments?.some((payment) => payment.PaymentSection === 'PY105') ||
      paymentStore.summary.splits.some((split) => split.method === PaymentMethod.Point),
  );

  watch(
    () => paymentStore.remainingAmount,
    async (newVal) => {
      // 포인트 결제 후 남은 결제 금액이 0원이 된 경우 결제 완료 처리
      if (newVal === 0) {
        // 주문 전송
        await order();
      }
    },
  );

  // onMounted(() => {
  //   // 결제 중에는 대기화면 인터벌 해제
  //   commonStore.clearWaitingPopupInterval();
  // });

  // onUnmounted(() => {
  //   // 결제 종료 후 대기화면 인터벌 재설정
  //   commonStore.setWaitingPopupInterval();
  // });

  /** 카드결제 클릭 시 나머지 금액 전체 결제 처리 후 주문 전송 */
  const handleCardClick = async () => await processCardPayment();

  /** 현금결제 클릭 시 나머지 금액 전체 결제 처리 후 주문 전송 */
  const handleCashClick = async () => await processCashPayment();

  /** 간편결제 클릭 시 나머지 금액 전체 결제 처리 후 주문 전송 */
  const handleEasyClick = async () => await processEasyPayment();

  /** 포인트결제 클릭 */
  const handlePointClick = async () => await processPointPayment();

  /**
   * 결제 취소 클릭
   * @param index 삭제할 결제 분할 인덱스
   */
  const handleCancelPaymentClick = async (index: number) => {
    const split = paymentStore.summary.splits[index];

    // 포인트 결제인 경우 사용 취소 API 호출
    if (split.method === PaymentMethod.Point) {
      const isOK = await commonStore.showDialog({
        type: 'confirm',
        message: t('label.cancel_point'),
      });
      if (!isOK) return;

      // 포인트 사용 취소 API 호출
      await useCancelPoints({
        CardNo: split.point!.CardNo,
        Amount: split.amount * -1,
      } satisfies RequestPoint);

      // 고객 데이터 초기화
      orderStore.setCustomer(null);
    }

    paymentStore.removePaymentSplit(index);
  };

  /** 이전 버튼 클릭 */
  const handlePrevClick = () => modalStore.goBackFullScreenModal();

  const processCardPayment = async (): Promise<boolean> => {
    // 남은 결제 금액 전체 카드 결제 진행
    const isSuccess = await modalStore.openSyncModalAsync<typeof PaymentCardModal, boolean>(PaymentCardModal, {});
    return isSuccess;
  };

  const processCashPayment = async (): Promise<boolean> => {
    const isOK = await commonStore.showDialog({
      type: 'confirm',
      title: t('label.take_order'),
      message: t('general.cash_pay_message'),
    });
    if (!isOK) return false;

    // 현금 결제 데이터 세팅
    paymentStore.addPaymentSplit(createPaymentSplit(PaymentMethod.Cash));
    return true;
  };

  const processEasyPayment = async (): Promise<boolean> => {
    // 남은 결제 금액 전체 간편 결제 진행
    const isSuccess = await modalStore.openSyncModalAsync<typeof PaymentEasyModal, boolean>(PaymentEasyModal, {});
    return isSuccess;
  };

  const processPointPayment = async (): Promise<void> => {
    const loadingId = commonStore.showLoading();
    try {
      // 현재 저장된 회원 정보 가져오기
      let customer;

      // 주문 데이터에 고객 데이터 존재 여부로 분기
      if (orderStore.order.CloudCustomer) {
        // 주문 데이터의 고객 휴대폰 번호로 회원 정보 다시 조회
        customer = await getCustomer(orderStore.order.CloudCustomer.Mobile);
      } else {
        // 주문 데이터에 고객 데이터가 없는 경우 회원 조회 모달 열어 회원 조회
        customer = await modalStore.openSyncModalAsync<typeof CustomerSearch, OrderCloudCustomer | null>(
          CustomerSearch,
          {
            type: 'USE',
          },
        );
      }

      // 회원 조회 실패한 경우 종료
      if (!customer) return;

      // 포인트 사용 모달 열기
      await modalStore.openSyncModalAsync<typeof PaymentPointModal, number>(PaymentPointModal, {
        customer,
      });
    } finally {
      commonStore.hideLoading(loadingId);
    }
  };

  /** 주문 진행 */
  const order = async () => {
    const loadingId = commonStore.showLoading();
    try {
      // POS에서 주문 조회 후 세팅
      await orderStore.fetchOrder(true);

      // 선결제인 경우 장바구니 데이터를 주문 데이터에 추가
      if (!orderStore.isPreOrder) {
        orderStore.addOrderItem(cartStore.cart.CartMenus);
      }

      // 모든 결제가 현금인지 여부 (주문 전송 후 결제 데이터 초기화 되기 때문에 주문 전송 이전에 위치해야함)
      const isCashOnly = paymentStore.summary.splits.every((split) => split.method === PaymentMethod.Cash);

      // 주문 전송
      await orderStore.sendOrder(true);

      // 모든 결제가 현금 결제인 경우 영수증 요청 생략하고 결제완료 모달 열기. 다른 결제가 있으면 영수증 모달 열기.
      if (isCashOnly) {
        modalStore.openFullScreenModal(OrderComplete, { hasPayment: false });
      } else {
        // 영수증 또는 주문완료 모달 열기
        openReceiptOrComplete(false);
      }
    } finally {
      commonStore.hideLoading(loadingId);
    }
  };
</script>

<style lang="scss" scoped>
  @use '@/assets/scss/variables' as vars;

  .payment-split {
    display: grid;
    grid-template-columns: 7fr 3fr;
    width: 100%;
    height: 100%;
    @media (max-width: vars.$sm) {
      grid-template-columns: 1fr 1fr;
    }

    .left {
      display: flex;
      flex-direction: column;
      justify-content: center;
      gap: 1.5625vw;
      height: 100vh;
      padding: 1.5625vw;
      background-color: #f3f4f7;
      @media (max-width: vars.$sm) {
        gap: 10px;
      }

      .title {
        font-size: 2.6042vw;
        font-weight: 500;
        @media (max-width: vars.$sm) {
          font-size: 24px;
          margin-bottom: 10px;
        }
      }

      .button-list {
        display: grid;
        grid-template-columns: repeat(4, minmax(0, 1fr));
        grid-template-rows: 1fr 1fr;
        gap: 1.5625vw;
        overflow-y: auto;
        @media (max-width: vars.$sm) {
          grid-template-columns: 1fr;
        }
      }
    }

    .right {
      display: grid;
      grid-template-rows: auto 1fr auto;
      gap: 1.5625vw;
      padding: 4.6875vw 1.5625vw 1.5625vw;
      height: 100vh;
      @media (max-width: vars.$sm) {
        gap: 10px;
        padding: 30px 10px 10px;
      }

      .header {
        display: grid;
        grid-template-rows: auto auto;
        text-align: center;
        font-size: 2.6042vw;
        font-weight: 500;
        @media (max-width: vars.$sm) {
          font-size: 24px;
        }

        .amount {
          color: var(--point-color-secondary);
          font-size: 2.9688vw;
          @media (max-width: vars.$sm) {
            font-size: 26px;
          }
        }
      }

      .main {
        display: grid;
        grid-auto-flow: row; /* 세로 배치 */
        grid-auto-rows: auto; /* 각 row는 자식 내용에 맞춤 */
        align-content: start; /* 위에서부터 채우기 */
        gap: 1.5625vw;
        height: 100%;
        padding: 0.7813vw 0;
        overflow-y: auto;

        li {
          display: grid;
          grid-template-columns: 1fr 6.25vw;
          width: 100%;
          border: 1px solid #e2e2e2;
          border-radius: 1.5625vw;
          @media (max-width: vars.$sm) {
            grid-template-columns: 1fr 40px;
          }

          .payment-text {
            padding: 1.5625vw;
            font-size: 2.0833vw;
            font-weight: 500;
            @media (max-width: vars.$sm) {
              font-size: 20px;
            }

            .amount {
              font-size: 2.3438vw;
              @media (max-width: vars.$sm) {
                font-size: 22px;
              }
            }
          }

          .cancel-button {
            border: none;
            border-left: 1px solid #e2e2e2;
            img {
              width: 2.3438vw;
              @media (max-width: vars.$sm) {
                width: 20px;
              }
            }
          }
        }
      }

      .footer {
        display: grid;
        grid-template-rows: auto;
        gap: 1.5625vw;

        .rest-amount {
          display: grid;
          grid-template-columns: auto 1fr;
          justify-content: space-between;
          font-size: 2.0833vw;
          font-weight: 500;
          @media (max-width: vars.$sm) {
            font-size: 20px;
          }

          .amount {
            color: var(--point-color-secondary);
            font-size: 2.3438vw;
            text-align: right;
            @media (max-width: vars.$sm) {
              font-size: 22px;
            }
          }
        }
      }
    }
  }
</style>
